// ============================================================================
// REGROUP TRANSACTION HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { regroupService } from 'api/services/regroupService';
import type { RegroupTransaction, CreateRegroupPayload, UpdateRegroupPayload } from 'api/types/regroup.types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'regroups';

export const useRegroups = () => {
  const [data, setData] = useState<RegroupTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRegroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await regroupService.getAll({ pageSize: 50000 });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegroups();
  }, [fetchRegroups]);

  useDataChangeListener(DATA_KEY, fetchRegroups);

  return { data, loading, error, refetch: fetchRegroups };
};

export const useCreateRegroup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createRegroup = useCallback(async (payload: CreateRegroupPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await regroupService.create(payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createRegroup, loading, error };
};

export const useUpdateRegroup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateRegroup = useCallback(async (id: string, payload: UpdateRegroupPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await regroupService.update(id, payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateRegroup, loading, error };
};

export const useSubmitRegroup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submitRegroup = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await regroupService.submit(id);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submitRegroup, loading, error };
};

export const useDeleteRegroup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteRegroup = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await regroupService.delete(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteRegroup, loading, error };
};
